import React, { useState } from 'react';
import { X, Unlock, ArrowRight, Sparkles, Gift, CheckCircle, Loader2, PartyPopper, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PricingUnlockModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    organisation: '',
    website: ''
  });
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  // Validate Australian phone number
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    const mobileRegex = /^(04\d{8}|(\+61|0061)4\d{8})$/;
    const landlineRegex = /^(0[2-9]\d{8}|(\+61|0061)[2-9]\d{8})$/;
    return mobileRegex.test(cleaned) || landlineRegex.test(cleaned);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid Australian phone number';
    }
    if (!formData.organisation.trim()) newErrors.organisation = 'Organisation is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pricing-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          organisation: formData.organisation,
          website: formData.website,
          source: 'pricing_unlock'
        })
      });

      if (!response.ok) throw new Error('Failed to submit');

      // Store in localStorage
      localStorage.setItem('contact_submitted', 'true');
      localStorage.setItem('contact_email', formData.email);
      
      setStep(2); // Success step
      
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2500);
      
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    // Real-time phone validation
    if (field === 'phone' && value && !validatePhone(value)) {
      setErrors(prev => ({ ...prev, phone: 'Please enter a valid Australian phone number' }));
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setFormData({ fullName: '', email: '', phone: '', organisation: '', website: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-bounceIn max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={resetAndClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all"
        >
          <X size={18} />
        </button>

        {/* Step 1: Form */}
        {step === 1 && (
          <>
            {/* Header */}
            <div className="bg-gradient-to-br from-tech-blue via-primary-navy to-care-teal p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Unlock className="text-white" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">View Investment Packages</h2>
              <p className="text-white/80 text-sm">Fill in your details to unlock pricing</p>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              {/* Full Name */}
              <div className={`relative transition-all duration-300 ${focusedField === 'fullName' ? 'scale-[1.02]' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  onFocus={() => setFocusedField('fullName')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all ${errors.fullName ? 'border-red-400 bg-red-50' : focusedField === 'fullName' ? 'border-tech-blue bg-tech-blue/5' : 'border-gray-200'}`}
                />
                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
              </div>

              {/* Email */}
              <div className={`relative transition-all duration-300 ${focusedField === 'email' ? 'scale-[1.02]' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="john@company.com.au"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all ${errors.email ? 'border-red-400 bg-red-50' : focusedField === 'email' ? 'border-tech-blue bg-tech-blue/5' : 'border-gray-200'}`}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div className={`relative transition-all duration-300 ${focusedField === 'phone' ? 'scale-[1.02]' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  placeholder="04XX XXX XXX"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all ${errors.phone ? 'border-red-400 bg-red-50' : focusedField === 'phone' ? 'border-care-teal bg-care-teal/5' : 'border-gray-200'}`}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Organisation */}
              <div className={`relative transition-all duration-300 ${focusedField === 'organisation' ? 'scale-[1.02]' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisation <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Your organisation"
                  value={formData.organisation}
                  onChange={(e) => handleInputChange('organisation', e.target.value)}
                  onFocus={() => setFocusedField('organisation')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all ${errors.organisation ? 'border-red-400 bg-red-50' : focusedField === 'organisation' ? 'border-tech-blue bg-tech-blue/5' : 'border-gray-200'}`}
                />
                {errors.organisation && <p className="text-red-500 text-xs mt-1">{errors.organisation}</p>}
              </div>

              {/* Organisation Website (Optional) */}
              <div className={`relative transition-all duration-300 ${focusedField === 'website' ? 'scale-[1.02]' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisation Website
                </label>
                <Input
                  type="url"
                  placeholder="https://www.yourcompany.com.au"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  onFocus={() => setFocusedField('website')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all ${focusedField === 'website' ? 'border-tech-blue bg-tech-blue/5' : 'border-gray-200'}`}
                />
              </div>

              <Button
                onClick={handleSubmit}
                disabled={loading || errors.phone}
                className="w-full bg-gradient-to-r from-tech-blue to-care-teal hover:from-tech-blue/90 hover:to-care-teal/90 text-white py-6 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Unlocking...
                  </>
                ) : (
                  <>
                    <Unlock className="mr-2" size={20} />
                    View Investment Packages
                  </>
                )}
              </Button>
            </div>

            {/* Benefits Footer */}
            <div className="px-6 pb-6">
              <div className="bg-gradient-to-r from-tech-blue/10 to-care-teal/10 rounded-xl p-4 border border-tech-blue/20">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="text-tech-blue" size={18} />
                  <span className="text-sm font-semibold text-primary-navy">What you'll unlock:</span>
                </div>
                <ul className="space-y-1">
                  <li className="flex items-center gap-2 text-xs text-text-secondary">
                    <CheckCircle className="text-care-teal" size={12} />
                    Full pricing for Starter, Growth & Enterprise
                  </li>
                  <li className="flex items-center gap-2 text-xs text-text-secondary">
                    <CheckCircle className="text-care-teal" size={12} />
                    Detailed feature comparison across all tiers
                  </li>
                  <li className="flex items-center gap-2 text-xs text-text-secondary">
                    <CheckCircle className="text-care-teal" size={12} />
                    No hidden fees - transparent one-time investment
                  </li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* Step 2: Success */}
        {step === 2 && (
          <div className="p-8 text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-care-teal to-tech-blue rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <PartyPopper className="text-white" size={48} />
            </div>
            <h2 className="text-2xl font-bold text-primary-navy mb-3">Success!</h2>
            <p className="text-text-secondary mb-4">
              Thank you, <span className="font-semibold text-tech-blue">{formData.fullName}</span>!
            </p>
            <p className="text-text-secondary mb-6">
              Pricing is now unlocked. Loading your exclusive rates...
            </p>
            <div className="flex justify-center gap-2">
              <div className="w-3 h-3 rounded-full bg-care-teal animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-3 h-3 rounded-full bg-tech-blue animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-3 h-3 rounded-full bg-primary-navy animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        )}

        <style jsx>{`
          @keyframes bounceIn {
            0% { opacity: 0; transform: scale(0.8); }
            50% { transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }
          .animate-bounceIn {
            animation: bounceIn 0.5s ease-out;
          }
        `}</style>
      </div>
    </div>
  );
};

export default PricingUnlockModal;
